import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Dimensions,
  LayoutChangeEvent,
  Modal,
  PanResponder,
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { primitive } from 'styles/constants/primitives';
import { isWeb } from 'utils/environment';

import { BottomSheetProps } from './types';

const ANIMATION_DURATION = 250;
const SWIPE_THRESHOLD = 0.3; // 30% of sheet height to dismiss
const BACKDROP_OPACITY = 0.5;

export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  onOpenEventMessage,
  children,
  contentStyle,
  showDragIndicator = !isWeb,
  closeOnBackdropPress = true,
  testID,
}) => {
  const { height: windowHeight } = useWindowDimensions();
  const { bottom: safeAreaBottom } = useSafeAreaInsets();
  const [contentHeight, setContentHeight] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const hasAnimatedOpen = useRef(false); // Track if initial open animation has run
  const isClosing = useRef(false); // Track if close animation is in progress

  const translateY = useSharedValue(windowHeight);
  const backdropOpacity = useSharedValue(0);
  const sheetOpacity = useSharedValue(0); // Start invisible to prevent Android flash

  // Callback to hide modal after close animation completes
  const hideModalAfterAnimation = useCallback(() => {
    if (isClosing.current) {
      isClosing.current = false;
      translateY.value = windowHeight;
      sheetOpacity.value = 0; // Reset opacity for next open
      setIsVisible(false);
    }
  }, [translateY, sheetOpacity, windowHeight]);

  // Handle open/close animations - only triggered by isOpen changes
  useEffect(() => {
    if (isOpen) {
      // Cancel any pending close
      isClosing.current = false;
      setIsVisible(true);

      // NOTE: We do NOT trigger the animation here immediately.
      // On Android, starting the animation before the Modal has fully laid out
      // can cause a "flash" from the top-left (width=0 or unstable layout).
      // Instead, we wait for a valid onLayout in handleContainerLayout before
      // starting the animation (setting sheetOpacity=1).
    } else if (isVisible) {
      // Only start close animation if currently visible
      isClosing.current = true;
      hasAnimatedOpen.current = false; // Reset for next open

      const targetY = contentHeight || windowHeight;
      translateY.value = withTiming(
        targetY,
        { duration: ANIMATION_DURATION, easing: Easing.out(Easing.ease) },
        finished => {
          'worklet';
          if (finished) {
            runOnJS(hideModalAfterAnimation)();
          }
        }
      );
      backdropOpacity.value = withTiming(0, { duration: ANIMATION_DURATION });
    }
  }, [
    isOpen,
    windowHeight,
    translateY,
    backdropOpacity,
    sheetOpacity,
    isVisible,
    contentHeight,
    hideModalAfterAnimation,
  ]);

  const handleClose = useCallback(() => {
    translateY.value = withTiming(contentHeight || windowHeight, {
      duration: ANIMATION_DURATION,
    });
    backdropOpacity.value = withTiming(0, { duration: ANIMATION_DURATION });
    setTimeout(() => {
      onClose?.();
    }, ANIMATION_DURATION);
  }, [contentHeight, windowHeight, translateY, backdropOpacity, onClose]);

  // Dedicated PanResponder for drag handle area
  // This captures gestures from the start on the drag indicator
  const dragHandlePanResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true, // Capture touch on drag handle immediately
        onPanResponderMove: (_, gestureState) => {
          // Only allow downward drag
          if (gestureState.dy > 0) {
            translateY.value = gestureState.dy;
          }
        },
        onPanResponderRelease: (_, gestureState) => {
          const shouldDismiss =
            gestureState.dy > contentHeight * SWIPE_THRESHOLD || gestureState.vy > 0.5;

          if (shouldDismiss) {
            runOnJS(handleClose)();
          } else {
            translateY.value = withTiming(0, { duration: ANIMATION_DURATION });
          }
        },
      }),
    [contentHeight, translateY, handleClose]
  );

  const sheetAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
    opacity: sheetOpacity.value,
  }));

  const backdropAnimatedStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value,
  }));

  const handleContentLayout = useCallback(
    (event: { nativeEvent: { layout: { height: number } } }) => {
      const { height } = event.nativeEvent.layout;
      if (height > 0 && height !== contentHeight) {
        setContentHeight(height);
      }
    },
    [contentHeight]
  );

  const handleContainerLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { width } = event.nativeEvent.layout;

      // Trigger animation only if:
      // 1. We should be open
      // 2. We haven't animated yet
      // 3. We have a valid layout (width > 0 to avoid left-side flash/glitch)
      if (isOpen && !hasAnimatedOpen.current && width > 0) {
        hasAnimatedOpen.current = true;

        logRBIEvent({
          name: CustomEventNames.ACTION_SHEET_VIEW,
          type: EventTypes.Other,
          attributes: {
            actionSheetHeader: onOpenEventMessage,
          },
        });

        sheetOpacity.value = 1;
        translateY.value = withTiming(0, { duration: ANIMATION_DURATION });
        backdropOpacity.value = withTiming(BACKDROP_OPACITY, { duration: ANIMATION_DURATION });
      }
    },
    [isOpen, onOpenEventMessage, translateY, sheetOpacity, backdropOpacity]
  );

  const handleBackdropPress = useCallback(() => {
    if (closeOnBackdropPress) {
      handleClose();
    }
  }, [closeOnBackdropPress, handleClose]);

  if (!isVisible && !isOpen) {
    return null;
  }

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="none"
      onRequestClose={handleClose}
      statusBarTranslucent
    >
      <View style={styles.container} testID={testID}>
        {/* Backdrop */}
        <Animated.View style={[styles.backdrop, backdropAnimatedStyle]}>
          <Pressable style={styles.backdropPressable} onPress={handleBackdropPress} />
        </Animated.View>

        {/* Sheet */}
        <Animated.View
          style={[styles.sheetContainer, sheetAnimatedStyle]}
          onLayout={handleContainerLayout}
        >
          <View
            style={[styles.sheet, { paddingBottom: safeAreaBottom || 16 }, contentStyle]}
            onLayout={handleContentLayout}
          >
            {showDragIndicator && (
              <View style={styles.dragIndicatorContainer} {...dragHandlePanResponder.panHandlers}>
                <View style={styles.dragIndicator} />
              </View>
            )}
            {children}
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'black',
  },
  backdropPressable: {
    flex: 1,
  },
  sheetContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
  },
  sheet: {
    backgroundColor: 'white',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 16,
    paddingTop: 8,
    minHeight: 100,
    maxHeight: Dimensions.get('window').height * 0.9,
  },
  dragIndicatorContainer: {
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 40, // Larger touch area for swipe gesture
  },
  dragIndicator: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: primitive.bk.$bbqBrown,
    opacity: 0.3,
  },
});

export default BottomSheet;
